"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useState } from "react";
import type { IconType } from "react-icons";
import { FiArrowRight, FiCalendar, FiLink, FiVideo } from "react-icons/fi";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { VIRTUAL_SERVICES_GUIDE_URL, hasSeenVirtualServicesGuide, markNotSuggested, markVirtualServicesGuideSeen, wasRecentlyNotSuggested } from "@/lib/virtual-services-guide";
import type { VirtualServicesGuide } from "@/types/service";
import { getVirtualServicesGuide } from "@/api/provider/admin/service";
type Step = {
  icon: IconType;
  text: string;
};
const STEPS: Step[] = [{
  icon: FiVideo,
  text: "Mark the services you do by video as virtual."
}, {
  icon: FiCalendar,
  text: "Book them at your clinic, during the provider's shift."
}, {
  icon: FiLink,
  text: "Patients still get their video link, and the visit still bills as telehealth."
}];
type Slot = "visit" | "video" | null;
function ShiftRow(t0) {
  const $ = _c(7);
  const {
    label,
    slots
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="w-12 shrink-0 text-caption text-foreground-secondary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== slots) {
    t2 = slots ? <div className="flex h-7 flex-1 gap-1 rounded-md border border-border bg-background-secondary p-1">{slots.map(_temp)}</div> : <div className="h-7 flex-1 rounded-md border border-dashed border-border" />;
    $[2] = slots;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

/**
 * A provider's day before and after: the video visit leaves the virtual shift
 * and books inside the clinic shift, so the virtual shift goes empty.
 */
function _temp(slot, index) {
  return <span key={index} className={cx("flex flex-1 items-center justify-center rounded-sm", slot === "visit" && "bg-status-success-bg", slot === "video" && "bg-status-info-bg text-status-info-text")}>{slot === "video" && <FiVideo size={12} />}</span>;
}
function ShiftsBeforeAfter() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="text-caption-strong text-foreground-secondary">Now: two shifts</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ShiftRow label="Clinic" slots={["visit", "visit", null]} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="space-y-2 rounded-lg border border-border p-3">{t0}{t1}<ShiftRow label="Virtual" slots={[null, null, "video"]} /></div>;
    t3 = <FiArrowRight className="mx-auto rotate-90 text-foreground-tertiary sm:rotate-0" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-caption-strong text-foreground-secondary">With virtual services</p>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div aria-hidden={true} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[1fr_auto_1fr]">{t2}{t3}<div className="space-y-2 rounded-lg border border-border p-3">{t4}<ShiftRow label="Clinic" slots={["visit", "visit", "video"]} /><ShiftRow label="Virtual" slots={null} /></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function quoted(names: string[]) {
  return names.map(name => `“${name}”`).join(" and ");
}
const BENEFIT = "With virtual services, a provider can take video visits during an in-person shift, and patients booking online don't pick a location.";

/** What the window says about what in the clinic's setup suggests virtual services. */
function promptCopy({
  reason,
  examples
}: VirtualServicesGuide) {
  if (reason === "virtual_location" && examples.length > 0) {
    return {
      title: "Try virtual services for your video visits",
      description: `You book video visits at ${examples[0]}. ${BENEFIT}`,
      showsShifts: false
    };
  }
  if (reason === "telehealth_services" && examples.length > 0) {
    const verb = examples.length > 1 ? "look like video visits" : "looks like a video visit";
    return {
      title: "Try virtual services for your video visits",
      description: `${quoted(examples)} ${verb}. ${BENEFIT}`,
      showsShifts: false
    };
  }
  return {
    title: "Book video visits on in-person shifts",
    description: "Your providers have shifts at a virtual location at the same time as their in-person shifts. With virtual services, a video visit books on the provider's in-person shift instead, so there's no second shift to keep in step.",
    showsShifts: true
  };
}

/**
 * Points a clinic that does video visits to the virtual services guide: its
 * providers work a shift at a virtual location over an in-person one (how
 * video visits during office hours were booked before virtual services), it
 * books visits at a virtual location, or its services are named like video
 * visits. Shown once per person, to staff who can edit services, since they
 * set it up.
 */
export function VirtualServicesGuidePrompt() {
  const $ = _c(51);
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const subDomain = tenant?.subDomain;
  const userId = currentUser?.id;
  let t0;
  if ($[0] !== currentUser) {
    t0 = hasPermission(currentUser, PERMISSIONS.SETTINGS_SERVICES_WRITE);
    $[0] = currentUser;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canEditServices = t0;
  const [seen, setSeen] = useState(false);
  let t1;
  if ($[2] !== seen || $[3] !== subDomain || $[4] !== userId) {
    t1 = !!subDomain && !!userId && !seen && !hasSeenVirtualServicesGuide(subDomain, userId);
    $[2] = seen;
    $[3] = subDomain;
    $[4] = userId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const unseen = t1;
  let t2;
  if ($[6] !== subDomain) {
    t2 = !!subDomain && wasRecentlyNotSuggested(subDomain);
    $[6] = subDomain;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const recentlyNotSuggested = t2;
  let t3;
  let t4;
  if ($[8] !== subDomain) {
    t3 = ["virtual-services-guide", subDomain];
    t4 = async () => {
      const guide = await getVirtualServicesGuide();
      if (guide && !guide.suggested && subDomain) {
        markNotSuggested(subDomain);
      }
      return guide;
    };
    $[8] = subDomain;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  const t5 = canEditServices && unseen && !recentlyNotSuggested;
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      staleTime: Infinity
    };
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    data
  } = useQuery(t6);
  const open = unseen && data?.suggested === true;
  let t7;
  if ($[15] !== data) {
    t7 = data ? promptCopy(data) : null;
    $[15] = data;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const copy = t7;
  let t8;
  if ($[17] !== data?.reason || $[18] !== subDomain || $[19] !== userId) {
    t8 = action => {
      if (subDomain && userId) {
        markVirtualServicesGuideSeen(subDomain, userId);
      }
      setSeen(true);
      posthog.capture("virtual_services_guide_prompt", {
        action,
        reason: data?.reason
      });
    };
    $[17] = data?.reason;
    $[18] = subDomain;
    $[19] = userId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const close = t8;
  let t9;
  if ($[21] !== close) {
    t9 = isOpen => {
      if (!isOpen) {
        close("dismissed");
      }
    };
    $[21] = close;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="mb-2 flex size-10 items-center justify-center rounded-full bg-status-info-bg text-status-info-text"><FiVideo size={20} /></span>;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = copy?.title;
  let t12;
  if ($[24] !== t11) {
    t12 = <DialogTitle>{t11}</DialogTitle>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = copy?.description;
  let t14;
  if ($[26] !== t13) {
    t14 = <DialogDescription>{t13}</DialogDescription>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t12 || $[29] !== t14) {
    t15 = <DialogHeader>{t10}{t12}{t14}</DialogHeader>;
    $[28] = t12;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== copy?.showsShifts) {
    t16 = copy?.showsShifts && <ShiftsBeforeAfter />;
    $[31] = copy?.showsShifts;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ul className="space-y-2">{STEPS.map(_temp2)}</ul>;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t16) {
    t18 = <DialogBody className="space-y-4">{t16}{t17}</DialogBody>;
    $[34] = t16;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== close) {
    t19 = <Button variant="secondary" onClick={() => close("dismissed")}>Not now</Button>;
    $[36] = close;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== close) {
    t20 = <Button variant="primary" asChild={true}><a href={VIRTUAL_SERVICES_GUIDE_URL} target="_blank" rel="noopener noreferrer" onClick={() => close("opened_guide")}>Read the guide</a></Button>;
    $[38] = close;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = <DialogFooter>{t19}{t20}</DialogFooter>;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t15 || $[44] !== t18 || $[45] !== t21) {
    t22 = <DialogContent className="sm:max-w-lg">{t15}{t18}{t21}</DialogContent>;
    $[43] = t15;
    $[44] = t18;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== open || $[48] !== t22 || $[49] !== t9) {
    t23 = <Dialog open={open} onOpenChange={t9}>{t22}</Dialog>;
    $[47] = open;
    $[48] = t22;
    $[49] = t9;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
}
function _temp2(t0) {
  const {
    icon: Icon,
    text
  } = t0;
  return <li key={text} className="flex items-center gap-3"><span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-background-secondary text-foreground-secondary"><Icon size={14} /></span><span className="text-body text-foreground">{text}</span></li>;
}